import React, { Component, useEffect } from "react";
import { Link } from "react-router-dom";
import MetaTags from 'react-meta-tags';
import withSizes from "react-sizes";

import { Header, TitleBanner, Category, SectionCareer, Button } from "../../components";

import style from "./Projects.module.scss";
import pattern from "../../assets/patterns/pattern02.svg";
import { useDispatch, useSelector } from 'react-redux';
import useLocale from "../../hooks/useLocale";
import { loadProjectsData } from "../../store/slices/projects";
import { normalizeMedia, mapSeo } from "../../api/strapi";

const ALL_CATEGORY_ID = 10010

function HookWrapper({ render }) {
  const locale = useLocale();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadProjectsData(locale));
  }, [dispatch, locale]);

  return render(loadCategory(locale), loadProjects(), loadPageBanner(), loadSeo(), loadGlossary(), locale);
}

function loadGlossary() {
  const { data } = useSelector(state => state.global)
  return (data && data.glossary) || {}
}

function loadPageBanner() {
  const { data } = useSelector(state => state.projects)
  const banner = data && data.page && data.page.banner
  return {
    title: (banner && banner.bannerTitle) || null,
    image: (normalizeMedia(banner && banner.bannerImage) || {}).file_url || null,
  }
}

function loadCategory(locale) {
  const { data } = useSelector(state => state.projects)
  const items = (data && data.items) || []
  const All = locale === 'id' ? 'Semua' : 'All'

  const seen = new Map()
  items.forEach(el => {
    if (el.category && !seen.has(el.category.id)) {
      seen.set(el.category.id, el.category.title)
    }
  })

  return [{ id: ALL_CATEGORY_ID, category: All }, ...Array.from(seen, ([id, title]) => ({ id, category: title }))]
}

function loadProjects() {
  const { data } = useSelector(state => state.projects)
  const items = (data && data.items) || []

  const forAll = items.map(el => ({
    id: el.id,
    categoryId: ALL_CATEGORY_ID,
    categoryId2: el.category && el.category.id,
    project_logo: (normalizeMedia(el.image) || {}).file_url,
    project_thumb: (normalizeMedia(el.image2) || {}).file_url,
    slug: el.slug,
  }))
  const perCategory = items.map(el => ({
    id: el.id,
    categoryId: el.category && el.category.id,
    categoryId2: el.category && el.category.id,
    project_logo: (normalizeMedia(el.image) || {}).file_url,
    project_thumb: (normalizeMedia(el.image2) || {}).file_url,
    slug: el.slug,
  }))
  return [...forAll, ...perCategory]
}

function loadSeo() {
  const { data } = useSelector(state => state.projects)
  return mapSeo(data && data.page && data.page.seo)
}

class Projects extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeCategory: 0,
      page: 1,
      totalPage: 0,
      perPage: 12,
      showedProjects: [],
      dataProjects: [],
    }
  }

  _getCatName = (categoryId, cats) => {
    let name = ''
    cats.forEach(it => {
      if (it.id === categoryId) {
        name = it.category
      }
    });
    return name;
  }

  _renderProjectsCard = (el, categories, key, locale) => {
    if (el.categoryId === this.state.activeCategory) {
      return (
        <Link to={`/${locale}/project-details/${el.slug}`} className={style.project__link} key={key}>
          <div className={style.project__card}>
            <img src={el.project_thumb} className={style.project__thumbnail} alt="" />
            <img src={el.project_logo} className={style.project__logo} alt="" />
            <div className={style.project__category}>{this._getCatName(el.categoryId2, categories)}</div>
          </div>
        </Link>
      )
    }
  }

  _setActive = (data) => {
    this.setState({
      activeCategory: data,
      dataProjects: []
    })
  }

  _process(projectsData) {
    if (projectsData.length > 0) {
      let projects = projectsData.filter(_el => {
        return (_el.categoryId === this.state.activeCategory)
      })

      if (this.state.dataProjects.length === 0) {
        this.setState({
          dataProjects: projects,
        }, () => {
          this._loadMore(this.props.isMobile, 1)
        })
      }
    }
  }

  _loadMore(isMobile, page = 1) {
    let perPage = 12
    if (isMobile <= 768 && isMobile > 500) {
      perPage = 9
    } else if (isMobile <= 500) {
      perPage = 3
    }
    let totalShowed = perPage * page
    let totalPage = Math.ceil(this.state.dataProjects.length / perPage)
    let showedProjects = this.state.dataProjects.filter((project, index) => index < totalShowed)
    this.setState({
      showedProjects,
      totalPage,
      page
    })
  }

  currentCategory = (categoryData) => {
    if (this.state.activeCategory === 0) {
      if (categoryData.length > 0) {
        let category = categoryData[0]
        this.setState({
          activeCategory: category.id,
        })
        return category.id
      } else {
        return 1
      }
    } else {
      return this.state.activeCategory
    }
  }

  render() {
    const { showedProjects, page, totalPage } = this.state;
    const { isMobile } = this.props;

    return (
      <HookWrapper render={(categoryData, projectsData, pageBanner, seo, glossary, locale) =>
        <>
          {this._process(projectsData)}
          <MetaTags>
            <title>{seo.title}</title>
            <meta name="description" content={seo.meta_description} />
            <meta name="keyword" content={seo.meta_keyword} />
            <meta property="og:title" content={seo.title} />
          </MetaTags>
          <Header />
          <TitleBanner
            title={pageBanner.title || glossary.projects || 'Projects'}
            image={pageBanner.image}
            pattern={pattern}
            backgroundSize={100}
          />
          <div className={style.projects__content}>
            <div className={style.projects__inner}>
              <Category category={categoryData} active={this.currentCategory(categoryData)} onClick={this._setActive} />
              <div className={style.project__list}>
                {
                  showedProjects.map((_el, index) => (
                    <React.Fragment key={index}>{this._renderProjectsCard(_el, categoryData, index, locale)}</React.Fragment>
                  ))
                }
              </div>
              <div className={style.button__panel}>
                {
                  page < totalPage && (<Button onClick={() => this._loadMore(isMobile, this.state.page + 1)} buttonName={glossary.loadMore || 'Load More'} full />)
                }
              </div>
            </div>
          </div>
          <SectionCareer locale={locale} />
        </>
      } />
    )
  }
}

const mapStateToProps = ({ width }) => ({
  isMobile: width,
})

export default withSizes(mapStateToProps)(Projects);
